import React, { useRef, useCallback } from 'react';
import { X, Link2, Check, Download } from 'lucide-react';
import { toast } from 'sonner';

/* ─── SVG brand icons ────────────────────────────────────────────────────── */
function WhatsAppIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
function FacebookIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
    </svg>
  );
}
function InstagramIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

/* ─── Canvas card generator for Instagram ───────────────────────────────── */
async function generateShareCard(property) {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(0, 0, 1080, 1080);

  // Property image
  if (property.images?.[0]) {
    try {
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          // cover-fill
          const iw = img.width, ih = img.height;
          const scale = Math.max(1080 / iw, 1080 / ih);
          const sw = iw * scale, sh = ih * scale;
          ctx.drawImage(img, (1080 - sw) / 2, (1080 - sh) / 2, sw, sh);
          resolve();
        };
        img.onerror = reject;
        img.src = property.images[0];
      });
    } catch (_) { /* keep dark background */ }
  }

  // Dark gradient overlay (bottom 60%)
  const grad = ctx.createLinearGradient(0, 400, 0, 1080);
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(0.35, 'rgba(0,0,0,0.6)');
  grad.addColorStop(1, 'rgba(0,0,0,0.93)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1080, 1080);

  // ── Top-right Rent Mitra badge ──
  ctx.fillStyle = '#1D4ED8';
  roundRect(ctx, 820, 40, 220, 56, 14);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 26px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Rent Mitra', 930, 76);

  // ── Category chip ──
  if (property.property_category) {
    const cat = property.property_category.toUpperCase();
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    roundRect(ctx, 50, 660, ctx.measureText(cat).width + 50, 44, 22);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 22px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(cat, 75, 689);
  }

  // ── Title ──
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 58px sans-serif';
  ctx.textAlign = 'left';
  const titleText = property.title || `${property.bhk_type || ''} ${property.property_type || ''}`.trim();
  const titleLines = wrapText(ctx, titleText, 50, 760, 980, 70);
  titleLines.forEach((line, i) => ctx.fillText(line, 50, 760 + i * 68));

  // ── Location ──
  const locationY = 760 + titleLines.length * 68 + 20;
  ctx.fillStyle = '#94a3b8';
  ctx.font = '34px sans-serif';
  const loc = [property.area, property.city].filter(Boolean).join(', ');
  ctx.fillText(`📍 ${loc}`, 50, locationY);

  // ── Rent ──
  ctx.fillStyle = '#60a5fa';
  ctx.font = 'bold 62px sans-serif';
  const rentStr = `₹${Number(property.rent || 0).toLocaleString('en-IN')}/mo`;
  ctx.fillText(rentStr, 50, locationY + 78);

  // ── BHK / Furnishing chips ──
  let chipX = 50;
  const chipY = locationY + 140;
  const chips = [property.bhk_type, property.furnishing].filter(Boolean);
  chips.forEach(chip => {
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    const w = ctx.measureText(chip).width + 48;
    roundRect(ctx, chipX, chipY, w, 44, 22);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = '24px sans-serif';
    ctx.fillText(chip, chipX + 24, chipY + 28);
    chipX += w + 14;
  });

  // ── Bottom bar ──
  ctx.fillStyle = 'rgba(29,78,216,0.85)';
  ctx.fillRect(0, 996, 1080, 84);
  ctx.fillStyle = '#ffffff';
  ctx.font = '28px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Find your perfect home at rentmitrahub.com', 540, 1044);

  return canvas.toDataURL('image/png');
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, _x, _y, maxW, _lineH) {
  const words = text.split(' ');
  const lines = [];
  let current = '';
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxW) { lines.push(current); current = w; }
    else current = test;
    if (lines.length >= 2) break;
  }
  if (current && lines.length < 2) lines.push(current);
  return lines;
}

/* ─── Main Modal component ──────────────────────────────────────────────── */
export default function ShareListingModal({ property, onClose }) {
  const [copied, setCopied] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const previewRef = useRef(null);

  const pageUrl = `${window.location.origin}/property/${property.property_id.replace(/_/g, '-')}`;
  const rentStr = `₹${Number(property.rent || 0).toLocaleString('en-IN')}/mo`;
  const loc = [property.area, property.city].filter(Boolean).join(', ');
  const details = [property.bhk_type, property.property_type, property.furnishing].filter(Boolean).join(' · ');

  const waText = encodeURIComponent(
    `*${property.title || details}*\n📍 ${loc}\n💰 ${rentStr}${property.bhk_type ? `\n🏠 ${property.bhk_type} ${property.property_type || ''}` : ''}\n\n👉 View full details:\n${pageUrl}\n\n_Listed on Rent Mitra_`
  );

  const handleCopy = async () => {
    await navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleInstagram = useCallback(async () => {
    setGenerating(true);
    try {
      const dataUrl = await generateShareCard(property);
      const link = document.createElement('a');
      link.download = `rentmitra-${property.property_id}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Card downloaded! Open Instagram and post it as a Story or Post.');
    } catch {
      toast.error('Could not generate card. Try copying the link instead.');
    }
    setGenerating(false);
  }, [property]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-testid="share-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Share Listing
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors" data-testid="share-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Property Preview Card */}
        <div ref={previewRef} className="mx-5 mt-5 rounded-2xl overflow-hidden border border-slate-100 shadow-sm bg-slate-900" data-testid="share-preview-card">
          {property.images?.[0] ? (
            <div className="relative">
              <img src={property.images[0]} alt={property.title} className="w-full h-44 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white font-bold text-base line-clamp-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {property.title || details}
                </p>
                <p className="text-slate-300 text-xs mt-0.5 flex items-center gap-1">
                  <span>📍</span>{loc}
                </p>
              </div>
              <div className="absolute top-3 right-3 bg-blue-700 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                Rent Mitra
              </div>
            </div>
          ) : (
            <div className="h-24 bg-gradient-to-br from-blue-700 to-blue-900 flex items-center justify-center">
              <span className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Rent Mitra</span>
            </div>
          )}
          <div className="bg-white px-4 py-3 flex items-center justify-between">
            <div>
              {details && <p className="text-slate-500 text-xs">{details}</p>}
            </div>
            <p className="text-blue-700 font-bold text-base">{rentStr}</p>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="p-5 space-y-3">
          {/* WhatsApp */}
          <a
            href={`https://wa.me/?text=${waText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white px-4 py-3.5 rounded-2xl font-semibold text-sm transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
            data-testid="share-whatsapp"
          >
            <WhatsAppIcon className="w-5 h-5 flex-shrink-0" />
            <div className="text-left">
              <div className="font-bold">Share on WhatsApp</div>
              <div className="text-green-100 text-xs font-normal">With full property details + link</div>
            </div>
          </a>

          {/* Facebook */}
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 w-full bg-[#1877F2] hover:bg-[#166fe5] text-white px-4 py-3.5 rounded-2xl font-semibold text-sm transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
            data-testid="share-facebook"
          >
            <FacebookIcon className="w-5 h-5 flex-shrink-0" />
            <div className="text-left">
              <div className="font-bold">Share on Facebook</div>
              <div className="text-blue-200 text-xs font-normal">Property image shown automatically</div>
            </div>
          </a>

          {/* Instagram */}
          <button
            onClick={handleInstagram}
            disabled={generating}
            className="flex items-center gap-3 w-full text-white px-4 py-3.5 rounded-2xl font-semibold text-sm transition-all shadow-sm hover:shadow-md active:scale-[0.98] disabled:opacity-70"
            style={{ background: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
            data-testid="share-instagram"
          >
            <InstagramIcon className="w-5 h-5 flex-shrink-0" />
            <div className="text-left">
              <div className="font-bold">{generating ? 'Generating card…' : 'Save Card for Instagram'}</div>
              <div className="text-pink-100 text-xs font-normal">Downloads a ready-to-post 1080×1080 image</div>
            </div>
          </button>

          {/* Copy Link */}
          <button
            onClick={handleCopy}
            className="flex items-center gap-3 w-full bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-3.5 rounded-2xl font-semibold text-sm transition-all active:scale-[0.98]"
            data-testid="share-copy-link"
          >
            {copied ? <Check className="w-5 h-5 text-green-600 flex-shrink-0" /> : <Link2 className="w-5 h-5 flex-shrink-0" />}
            <div className="text-left">
              <div className="font-bold">{copied ? 'Copied!' : 'Copy Link'}</div>
              <div className="text-slate-400 text-xs font-normal">Share anywhere — SMS, email, Telegram…</div>
            </div>
          </button>
        </div>

        {/* Footer note */}
        <p className="text-center text-slate-400 text-xs pb-5 px-5">
          Facebook &amp; WhatsApp will show the listing image automatically when you share the link.
        </p>
      </div>
    </div>
  );
}
